import { Suspense, useState, useRef, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { graphql, useLazyLoadQuery, useFragment, useMutation } from 'react-relay';
import { formatDurationTimestamp } from '@/utils/format';
import type { EpisodeDetailPageQuery } from '@/__generated__/EpisodeDetailPageQuery.graphql';
import type { EpisodeDetailPage_episode$key } from '@/__generated__/EpisodeDetailPage_episode.graphql';
import type { EpisodeDetailPageUpdateMutation } from '@/__generated__/EpisodeDetailPageUpdateMutation.graphql';

/**
 * Query to fetch episode data for the detail page
 * Uses the node query with a constructed global ID
 */
const episodeDetailPageQuery = graphql`
  query EpisodeDetailPageQuery($id: ID!) {
    node(id: $id) {
      __typename
      ... on Episode {
        ...EpisodeDetailPage_episode
      }
    }
  }
`;

/**
 * Fragment for episode data
 */
const EpisodeDetailPage_episode = graphql`
  fragment EpisodeDetailPage_episode on Episode {
    id
    episodeId
    topicSlug
    title
    summary
    transcript
    publishedAt
    durationSeconds
    audioS3Key
    sourceLinks
    chapters {
      id
      title
      startTimeMs
      endTimeMs
      description
    }
  }
`;

/**
 * Mutation to update an episode
 */
const updateEpisodeMutation = graphql`
  mutation EpisodeDetailPageUpdateMutation($input: UpdateEpisodeInput!) {
    updateEpisode(input: $input) {
      episode {
        id
        ...EpisodeDetailPage_episode
      }
      userErrors {
        field
        message
        code
      }
    }
  }
`;

interface ChapterInput {
  id: string;
  title: string;
  startTimeMs: number;
  endTimeMs: number;
  description?: string;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatTime(seconds: number): string {
  if (!seconds || !Number.isFinite(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatTimeMs(ms: number): string {
  return formatTime(ms / 1000);
}

interface EpisodeDetailContentProps {
  episodeRef: EpisodeDetailPage_episode$key;
  onBack: () => void;
}

function EpisodeDetailContent({ episodeRef, onBack }: EpisodeDetailContentProps) {
  const episode = useFragment(EpisodeDetailPage_episode, episodeRef);
  const [commitUpdate, isUpdating] = useMutation<EpisodeDetailPageUpdateMutation>(updateEpisodeMutation);

  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(episode.title);
  const [editedSummary, setEditedSummary] = useState(episode.summary);
  const [editedChapters, setEditedChapters] = useState<ChapterInput[]>(
    episode.chapters.map((ch) => ({
      id: ch.id,
      title: ch.title,
      startTimeMs: ch.startTimeMs,
      endTimeMs: ch.endTimeMs,
      description: ch.description ?? undefined,
    }))
  );
  const [updateError, setUpdateError] = useState(false);

  // Player state
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const audioUrl = `https://kaster.media/${episode.audioS3Key}`;

  // Audio event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleDurationChange = () => setDuration(audio.duration);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);

    audio.src = audioUrl;

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [audioUrl]);

  // Find current chapter based on playback time
  const currentChapter = useMemo(() => {
    if (!episode.chapters?.length) return null;
    const currentTimeMs = currentTime * 1000;
    return (
      episode.chapters.find(
        (ch) => currentTimeMs >= ch.startTimeMs && currentTimeMs < ch.endTimeMs
      ) || null
    );
  }, [episode.chapters, currentTime]);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newTime = Number(e.target.value);
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const seekToChapter = (startTimeMs: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.currentTime = startTimeMs / 1000;
    setCurrentTime(startTimeMs / 1000);
    if (!isPlaying) {
      audio.play();
    }
  };

  const handleSave = () => {
    setUpdateError(false);
    commitUpdate({
      variables: {
        input: {
          topicSlug: episode.topicSlug,
          episodeId: episode.episodeId,
          title: editedTitle,
          summary: editedSummary,
          chapters: editedChapters,
        },
      },
      onCompleted: (response) => {
        if (response.updateEpisode.userErrors.length === 0) {
          setIsEditing(false);
        } else {
          setUpdateError(true);
          console.error('Update failed:', response.updateEpisode.userErrors);
        }
      },
      onError: (error) => {
        setUpdateError(true);
        console.error('Failed to update episode:', error);
      },
    });
  };

  const handleCancelEdit = () => {
    setEditedTitle(episode.title);
    setEditedSummary(episode.summary);
    setEditedChapters(
      episode.chapters.map((ch) => ({
        id: ch.id,
        title: ch.title,
        startTimeMs: ch.startTimeMs,
        endTimeMs: ch.endTimeMs,
        description: ch.description ?? undefined,
      }))
    );
    setIsEditing(false);
  };

  const updateChapter = (index: number, field: keyof ChapterInput, value: string | number) => {
    setEditedChapters((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={onBack}
          className="btn-secondary flex items-center gap-2"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-white">
            {isEditing ? 'Edit Episode' : 'Episode Details'}
          </h2>
        </div>
        {!isEditing && (
          <button type="button" onClick={() => setIsEditing(true)} className="btn-primary">
            Edit Episode
          </button>
        )}
      </div>

      {/* Audio Player */}
      <div className="card">
        <audio ref={audioRef} preload="metadata">
          <track kind="captions" />
        </audio>

        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={togglePlayPause}
            className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-indigo-600 text-white hover:bg-indigo-700"
          >
            {isPlaying ? (
              <svg className="h-7 w-7" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <rect x="6" y="4" width="4" height="16" />
                <rect x="14" y="4" width="4" height="16" />
              </svg>
            ) : (
              <svg className="h-7 w-7" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>

          <div className="flex-1">
            <div className="flex items-center justify-between text-sm text-gray-400 mb-2">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration || episode.durationSeconds)}</span>
            </div>
            <input
              type="range"
              min={0}
              max={duration || episode.durationSeconds}
              value={currentTime}
              onChange={handleSeek}
              className="w-full h-2 cursor-pointer appearance-none rounded-full bg-gray-700"
            />
          </div>
        </div>

        {currentChapter && (
          <div className="mt-3 text-sm text-gray-400">
            Now playing: <span className="text-white">{currentChapter.title}</span>
          </div>
        )}
      </div>

      {/* Episode Info */}
      <div className="card">
        <h3 className="text-lg font-semibold text-white mb-4">Episode Information</h3>

        {isEditing ? (
          <div className="space-y-4">
            <label className="block">
              <span className="block text-sm font-medium text-gray-300 mb-1">Title</span>
              <input
                type="text"
                className="input w-full"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-300 mb-1">Summary</span>
              <textarea
                className="input w-full min-h-[120px]"
                value={editedSummary}
                onChange={(e) => setEditedSummary(e.target.value)}
              />
            </label>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-400">Title</p>
              <p className="text-white">{episode.title}</p>
            </div>
            <div>
              <p className="text-sm text-gray-400">Summary</p>
              <p className="text-gray-300 whitespace-pre-wrap">{episode.summary}</p>
            </div>
            <div className="flex gap-8">
              <div>
                <p className="text-sm text-gray-400">Duration</p>
                <p className="text-white">{formatDurationTimestamp(episode.durationSeconds)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Published</p>
                <p className="text-white">{formatDate(episode.publishedAt)}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Chapters */}
      <div className="card">
        <h3 className="text-lg font-semibold text-white mb-4">Chapters</h3>

        {episode.chapters.length === 0 ? (
          <p className="text-gray-400">No chapters available</p>
        ) : isEditing ? (
          <div className="space-y-4">
            {editedChapters.map((chapter, index) => (
              <div key={chapter.id} className="rounded-lg border border-gray-700 p-4">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-sm text-gray-500">{index + 1}.</span>
                  <input
                    type="text"
                    className="input flex-1"
                    value={chapter.title}
                    onChange={(e) => updateChapter(index, 'title', e.target.value)}
                    placeholder="Chapter title"
                  />
                </div>
                <div className="flex gap-4 mb-3">
                  <label className="flex-1">
                    <span className="block text-xs text-gray-500 mb-1">Start Time (ms)</span>
                    <input
                      type="number"
                      className="input w-full text-sm"
                      value={chapter.startTimeMs}
                      onChange={(e) => updateChapter(index, 'startTimeMs', Number(e.target.value))}
                    />
                  </label>
                  <label className="flex-1">
                    <span className="block text-xs text-gray-500 mb-1">End Time (ms)</span>
                    <input
                      type="number"
                      className="input w-full text-sm"
                      value={chapter.endTimeMs}
                      onChange={(e) => updateChapter(index, 'endTimeMs', Number(e.target.value))}
                    />
                  </label>
                </div>
                <label className="block">
                  <span className="block text-xs text-gray-500 mb-1">Description (optional)</span>
                  <input
                    type="text"
                    className="input w-full text-sm"
                    value={chapter.description || ''}
                    onChange={(e) => updateChapter(index, 'description', e.target.value)}
                    placeholder="Chapter description"
                  />
                </label>
              </div>
            ))}
          </div>
        ) : (
          <ul className="space-y-2">
            {episode.chapters.map((chapter, index) => {
              const isActive = currentChapter?.id === chapter.id;
              return (
                <li key={chapter.id}>
                  <button
                    type="button"
                    onClick={() => seekToChapter(chapter.startTimeMs)}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-indigo-600/20 border border-indigo-500'
                        : 'bg-gray-800/50 hover:bg-gray-700/50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-sm text-gray-500 pt-0.5">{index + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p
                          className={`font-medium ${isActive ? 'text-indigo-400' : 'text-white'}`}
                        >
                          {chapter.title}
                        </p>
                        {chapter.description && (
                          <p className="text-sm text-gray-400 mt-0.5">{chapter.description}</p>
                        )}
                      </div>
                      <span className="text-sm text-gray-500 pt-0.5">
                        {formatTimeMs(chapter.startTimeMs)}
                      </span>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Sources */}
      {episode.sourceLinks.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-semibold text-white mb-4">Sources</h3>
          <ul className="space-y-2">
            {episode.sourceLinks.map((link) => (
              <li key={link}>
                <a
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-400 hover:text-indigo-300 text-sm break-all"
                >
                  {link}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Transcript (collapsed by default) */}
      <details className="card">
        <summary className="text-lg font-semibold text-white cursor-pointer">
          Transcript
        </summary>
        <div className="mt-4 text-gray-300 whitespace-pre-wrap text-sm max-h-96 overflow-y-auto">
          {episode.transcript}
        </div>
      </details>

      {/* Edit Actions */}
      {isEditing && (
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={handleCancelEdit}
            className="btn-secondary"
            disabled={isUpdating}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="btn-primary"
            disabled={isUpdating}
          >
            {isUpdating ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}

      {updateError && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-red-200">
          Failed to save changes. Please try again.
        </div>
      )}
    </div>
  );
}

/**
 * Encode an episode ID as a Relay global ID
 */
function encodeEpisodeId(topicSlug: string, episodeId: string): string {
  // Match the backend encoding: Episode:{topicSlug}:{episodeId} -> base64url
  const rawId = `Episode:${topicSlug}:${episodeId}`;
  // Base64 URL safe encoding (no padding)
  return btoa(rawId).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function EpisodeDetailPageContent({ topicSlug, episodeId, onBack }: {
  topicSlug: string;
  episodeId: string;
  onBack: () => void;
}) {
  const globalId = encodeEpisodeId(topicSlug, episodeId);
  const data = useLazyLoadQuery<EpisodeDetailPageQuery>(episodeDetailPageQuery, {
    id: globalId,
  });

  // The node query returns the episode via inline fragment
  const episode = data.node;

  if (!episode || episode.__typename !== 'Episode') {
    return (
      <div className="card text-center py-12">
        <p className="text-red-400">Failed to load episode</p>
        <button type="button" onClick={onBack} className="btn-secondary mt-4">
          Go Back
        </button>
      </div>
    );
  }

  return <EpisodeDetailContent episodeRef={episode} onBack={onBack} />;
}

export function EpisodeDetailPage() {
  const { topicSlug, episodeId } = useParams<{ topicSlug: string; episodeId: string }>();
  const navigate = useNavigate();

  if (!topicSlug || !episodeId) {
    return <Navigate to="/admin/episodes" replace />;
  }

  const handleBack = () => navigate(`/admin/episodes/${topicSlug}`);

  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
        </div>
      }
    >
      <EpisodeDetailPageContent
        topicSlug={topicSlug}
        episodeId={episodeId}
        onBack={handleBack}
      />
    </Suspense>
  );
}
